import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useForm } from '@sequencehq/utils'
import { SCHEDULE_COMPLETED_STATUSES } from 'BillingScheduleHome/billingScheduleHome.constants'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { dequal } from 'dequal'
import { GoCardlessInspectorItem } from 'Integrations/integrationsConfig/goCardless/InspectorItem/GoCardlessInspectorItem'
import { GoCardlessPaymentWidget } from 'Integrations/integrationsConfig/goCardless/InspectorItem/widgets/GoCardlessPaymentWidget'
import { getGoCardlessDisabledReason } from 'Integrations/utils/getGoCardlessDisabledReason'
import { useNotifications } from '@sequencehq/core-components'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'
import { useEffect, useMemo, useRef } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { usePermissions } from 'lib/rbac'
import { getBillingAddress } from '@sequencehq/api/utils/commonModels'

const useGoCardlessInspectorItem = (props: { billingScheduleId: string }) => {
  const { displayNotification } = useNotifications()
  const { can } = usePermissions()
  const isResettingAfterError = useRef(false)
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId: props.billingScheduleId,
    select: data => {
      return {
        rawSettingsData: data.billingScheduleSettings,
        billingScheduleStatus: data.billingSchedule.status,
        acceptPaymentsViaGoCardless:
          data?.billingScheduleSettings?.paymentProvider === 'GOCARDLESS',
        otherProviderActive:
          data?.billingScheduleSettings?.paymentProvider !== 'GOCARDLESS' &&
          data?.billingScheduleSettings?.paymentProvider !== 'NONE',
        currency: data.prices[0]?.currency,
        customerCountry: getBillingAddress(data.customer?.addresses)?.country,
        hasCustomer: Boolean(data.customer),
        hasProducts: data.prices.length > 0
      }
    }
  })

  const scheduleSettingsMutation = useMutation(
    dashboard20240730Client.putBillingScheduleSettings
  )

  const syncDataToService = useDebouncedCallback(
    (newData: { acceptPaymentsViaGoCardless: boolean }) => {
      if (!billingScheduleAggregateQuery.data?.rawSettingsData?.id) {
        return
      }

      scheduleSettingsMutation.mutate({
        id: billingScheduleAggregateQuery.data?.rawSettingsData?.id,
        body: {
          ...billingScheduleAggregateQuery.data?.rawSettingsData,
          paymentProvider: newData.acceptPaymentsViaGoCardless
            ? 'GOCARDLESS'
            : 'NONE',
          autoCharge: false
        }
      })
    },
    250
  )

  const initialFormValue = useMemo(
    () => ({
      acceptPaymentsViaGoCardless:
        billingScheduleAggregateQuery.data?.acceptPaymentsViaGoCardless ?? false
    }),
    [billingScheduleAggregateQuery]
  )

  const form = useForm({
    value: initialFormValue,
    fieldConfiguration: [
      {
        property: 'acceptPaymentsViaGoCardless'
      }
    ],
    onChange: (newData: { acceptPaymentsViaGoCardless: boolean }) => {
      if (isResettingAfterError.current) {
        isResettingAfterError.current = false
        return
      }

      syncDataToService({
        acceptPaymentsViaGoCardless: newData.acceptPaymentsViaGoCardless
      })
    }
  })

  useEffect(() => {
    if (
      scheduleSettingsMutation.error &&
      !dequal(initialFormValue, form.queries.formData)
    ) {
      // A failed write only needs a local rollback; persisting the reset would
      // issue a second mutation for state the server already has.
      isResettingAfterError.current = true
      form.mutators.resetFormData()
      scheduleSettingsMutation.reset()
      displayNotification(
        getApiErrorMessage(
          scheduleSettingsMutation.error,
          'Could not save changes to GoCardless settings'
        ),
        { type: 'error' }
      )
    }
  }, [
    scheduleSettingsMutation.reset,
    form.mutators.resetFormData,
    scheduleSettingsMutation.error,
    initialFormValue,
    form.queries.formData,
    displayNotification
  ])

  const canManageBilling = can('org.billing:update')

  const otherProviderActive =
    billingScheduleAggregateQuery.data?.otherProviderActive ?? false
  const currency = billingScheduleAggregateQuery.data?.currency
  const customerCountry = billingScheduleAggregateQuery.data?.customerCountry
  const hasCustomer = billingScheduleAggregateQuery.data?.hasCustomer ?? false
  const hasProducts = billingScheduleAggregateQuery.data?.hasProducts ?? false

  const disabledReason = useMemo(
    () =>
      getGoCardlessDisabledReason({
        otherProviderActive,
        currency,
        country: customerCountry,
        hasCustomer,
        hasProducts
      }),
    [otherProviderActive, currency, customerCountry, hasCustomer, hasProducts]
  )

  return {
    isLoading: billingScheduleAggregateQuery.isPending,
    isAvailable:
      canManageBilling &&
      billingScheduleAggregateQuery.data?.billingScheduleStatus &&
      !SCHEDULE_COMPLETED_STATUSES.includes(
        billingScheduleAggregateQuery.data?.billingScheduleStatus
      ),
    isMutating: scheduleSettingsMutation.isPending,
    disabledReason,
    formFields: form.fields
  }
}

export const BillingScheduleGoCardlessSettingsInspectorItem = (props: {
  billingScheduleId: string
}) => {
  const { formFields, isLoading, isMutating, isAvailable, disabledReason } =
    useGoCardlessInspectorItem(props)
  const { can } = usePermissions()
  const canManageBilling = can('org.billing:update')
  const isCurrentlyEnabled = formFields.acceptPaymentsViaGoCardless.value
  const isUpdating = isLoading || isMutating

  return (
    <GoCardlessInspectorItem
      alwaysShow
      enabled={formFields.acceptPaymentsViaGoCardless.value}
    >
      <GoCardlessPaymentWidget
        fieldData={{
          acceptPaymentsViaGoCardless: {
            value: formFields.acceptPaymentsViaGoCardless.value,
            disabled: isUpdating
          }
        }}
        onChange={newData =>
          formFields.acceptPaymentsViaGoCardless.onChange(
            newData.acceptPaymentsViaGoCardless.value
          )
        }
        disabled={
          isUpdating ||
          !canManageBilling ||
          (!isCurrentlyEnabled && (!isAvailable || disabledReason !== null))
        }
        disabledReason={disabledReason}
      />
    </GoCardlessInspectorItem>
  )
}
